import React, { useState } from "react";
import { DiYii } from "react-icons/di";

const TemplateAuth = () => {
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [codeDelivery, setCodeDelivery] = useState("zero-tap");
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(true);
  const [addExpiryTime, setAddExpiryTime] = useState(false);
  const [setCustomValidity, setSetCustomValidity] = useState(false);
  const [validityPeriod, setValidityPeriod] = useState("10");
  const [zeroTapAgreement, setZeroTapAgreement] = useState(false);

  const [apps, setApps] = useState([{ id: 1, packageName: "", signatureHash: "" }]);

  const addApp = () => {
    if (apps.length < 5) {
      setApps([...apps, { id: Date.now(), packageName: "", signatureHash: "" }]);
    }
  };

  const removeApp = (id) => {
    if (apps.length > 1) {
      setApps(apps.filter((app) => app.id !== id));
    }
  };

  const handleSubmit = () => {
    const components = [];

    const bodyComponent = { type: "BODY", add_security_recommendation: addSecurityRecommendation };
    components.push(bodyComponent);

    if (addExpiryTime || setCustomValidity) {
      components.push({
        type: "FOOTER",
        code_expiration_minutes: parseInt(setCustomValidity ? validityPeriod : "10"),
      });
    }

    if (codeDelivery === "zero-tap" || codeDelivery === "one-tap") {
      const buttonsComponent = { type: "BUTTONS", buttons: [] };

      apps.forEach((app) => {
        if (app.packageName && app.signatureHash) {
          buttonsComponent.buttons.push({
            type: "OTP",
            otp_type: codeDelivery === "zero-tap" ? "ZERO_TAP" : "ONE_TAP",
            text: "Copy Code",
            autofill_text: "Autofill",
            package_name: app.packageName,
            signature_hash: app.signatureHash,
          });
        }
      });

      if (buttonsComponent.buttons.length > 0) {
        components.push(buttonsComponent);
      }
    }

    const payload = {
      name: templateName.toLowerCase().replace(/\s+/g, "_"),
      language,
      category: "AUTHENTICATION",
      components,
    };

    // console.log("Authentication Template Payload:", JSON.stringify(payload, null, 2));
  };

  const getPreviewMessage = () => {
    let message =
      "{{1}} is your verification code. For your security, do not share this code.";
    if (addExpiryTime) {
      message += " This code will expire in {{2}} minutes.";
    }
    return message;
  };

  const getPreviewFooter = () => {
    return addSecurityRecommendation
      ? "This message was sent to verify your phone number. Message and data rates may apply."
      : null;
  };

  const showAutofillButton = () =>
    codeDelivery === "zero-tap" || codeDelivery === "one-tap";

  return (
    <div className="flex flex-wrap p-6 gap-8">
      <div className="w-full lg:w-1/2 bg-gray-100 p-6 rounded-lg shadow-lg">
        {/* Header */}
        <div className="w-full bg-white p-4 mb-2 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4 flex gap-4">
            <DiYii size={35} className="bg-green-600 p-1 rounded-lg text-white" />
            {templateName || "your_template_name"} •{" "}
            {language === "en_US" ? "English (US)" : language}
          </h2>
          <p className="text-sm text-gray-600">
            Authentication • One-time passcode
          </p>
        </div>

        <div className="space-y-6">
          {/* Template Name and Language */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-extrabold mb-4">Template name and language</h3>
            <div className="mb-4">
              <label className="block mb-2 font-medium">Name your template</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter a template name"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium">Select language</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-48 p-2 border rounded"
              >
                <option value="en_US">English (US)</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="ar">Arabic</option>
                <option value="hi">Hindi</option>
              </select>
            </div>
          </div>

          {/* Code Delivery Setup */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-extrabold mb-2">Code delivery setup</h3>
            <label className="flex items-start gap-3 mb-2">
              <input
                type="radio"
                checked={codeDelivery === "zero-tap"}
                onChange={() => setCodeDelivery("zero-tap")}
              />
              Zero-tap auto-fill
            </label>
            <label className="flex items-start gap-3 mb-2">
              <input
                type="radio"
                checked={codeDelivery === "one-tap"}
                onChange={() => setCodeDelivery("one-tap")}
              />
              One-tap auto-fill
            </label>
            <label className="flex items-start gap-3">
              <input
                type="radio"
                checked={codeDelivery === "copy"}
                onChange={() => setCodeDelivery("copy")}
              />
              Copy code
            </label>

            {codeDelivery === "zero-tap" && (
              <div className="mt-3 p-3 bg-gray-50 rounded border">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={zeroTapAgreement}
                    onChange={(e) => setZeroTapAgreement(e.target.checked)}
                  />
                  I agree to the WhatsApp Business Terms of Service.
                </label>
              </div>
            )}
          </div>

          {/* App Setup */}
          {showAutofillButton() && (
            <div className="bg-white p-4 rounded shadow">
              <h3 className="font-extrabold mb-2">App setup</h3>
              {apps.map((app, index) => (
                <div key={app.id} className="mb-4 p-3 border rounded bg-gray-50">
                  <input
                    type="text"
                    value={app.packageName}
                    onChange={(e) => {
                      const updated = [...apps];
                      updated[index].packageName = e.target.value;
                      setApps(updated);
                    }}
                    placeholder="Package name"
                    className="w-full p-2 border rounded mb-2"
                  />
                  <input
                    type="text"
                    value={app.signatureHash}
                    onChange={(e) => {
                      const updated = [...apps];
                      updated[index].signatureHash = e.target.value;
                      setApps(updated);
                    }}
                    placeholder="Signature hash"
                    className="w-full p-2 border rounded"
                  />
                  {apps.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeApp(app.id)}
                      className="text-red-500 text-sm mt-2"
                    >
                      Remove app
                    </button>
                  )}
                </div>
              ))}

              {apps.length < 5 && (
                <button
                  type="button"
                  onClick={addApp}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Add another app
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-extrabold mb-2">Content</h3>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={addSecurityRecommendation}
                onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
              />
              Add security recommendation
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={addExpiryTime}
                onChange={(e) => setAddExpiryTime(e.target.checked)}
              />
              Add expiry time
            </label>
          </div>

          {/* Validity Period */}
          <div className="bg-white p-4 rounded shadow">
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={setCustomValidity}
                onChange={(e) => setSetCustomValidity(e.target.checked)}
              />
              Set custom validity
            </label>
            {setCustomValidity && (
              <select
                value={validityPeriod}
                onChange={(e) => setValidityPeriod(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="5">5 minutes</option>
                <option value="10">10 minutes</option>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
              </select>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateAuth;
